import type { QueryClient } from '@tanstack/react-query';
import { ItemsApi } from '@/common/api/items';
import type { ItemDependency } from '@/common/api/types/items';
import { commonQueryKeys } from '@/common/queryKeys/common';
import type { PageData } from '@/features/items/components/table/hooks/useTablePagedQuery';

export const DependencyChangeKind = {
  Add: 'add',
  Remove: 'remove',
} as const;

export type DependencyChangeKind = (typeof DependencyChangeKind)[keyof typeof DependencyChangeKind];

const collectLoadedDependencies = (
  queryClient: QueryClient,
): ReadonlyMap<number, ItemDependency[]> => {
  const loaded = new Map<number, ItemDependency[]>();
  const pageQueries = queryClient.getQueryCache().findAll({
    queryKey: [commonQueryKeys.itemsTable.visibleDependencies],
    type: 'active',
  });
  for (const query of pageQueries) {
    const page = query.state.data as PageData<ItemDependency[]> | undefined;
    if (!page) continue;
    for (const [id, dependencies] of page.valuesById) loaded.set(id, dependencies);
  }
  return loaded;
};

const selectIdsToRefresh = (
  loaded: ReadonlyMap<number, ItemDependency[]>,
  changedIds: ReadonlySet<number>,
  kind: DependencyChangeKind,
): number[] => {
  if (kind === DependencyChangeKind.Add) return [...loaded.keys()];
  return [...loaded]
    .filter(
      ([id, dependencies]) =>
        changedIds.has(id) ||
        dependencies.some(
          (dependency) =>
            dependency.inherited === true &&
            dependency.inheritedFromItemId != null &&
            changedIds.has(dependency.inheritedFromItemId),
        ),
    )
    .map(([id]) => id);
};

export const refreshLoadedDependencies = async (
  queryClient: QueryClient,
  changedIds: Iterable<number>,
  kind: DependencyChangeKind,
): Promise<void> => {
  const ids = selectIdsToRefresh(collectLoadedDependencies(queryClient), new Set(changedIds), kind);
  if (ids.length === 0) return;

  const items = await ItemsApi.getItemsBulkFetchChunked({ ids, fields: ['DEPENDENCIES'] }).catch(
    () => undefined,
  );
  if (!items) return;

  const itemsById = new Map(items.map((item) => [item.id, item]));
  for (const id of ids) {
    queryClient.setQueryData(
      commonQueryKeys.dependencies.byItemId(id),
      itemsById.get(id)?.dependencies ?? [],
    );
  }
};
